import axios, { AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import intl from 'react-intl-universal';
import { get as getValue } from 'lodash';
import { getCurrentLocale } from '../../i18n';

import mobilMessage from '../../components/message';
import { message as pcMessage } from 'antd';
import { isMobile } from '@src/utils/is';

const message = isMobile() ? mobilMessage : pcMessage;
export const HTTP_ERROR_CODE = 10000;
export const CANCEL_CODE = 10001;

export interface RequestConfig extends AxiosRequestConfig {
    notifyBizError?: boolean; //网络请求抛出错误
    notifyHttpError?: boolean; //后端抛出错误
}

export interface ApiResult<T = any> {
    code: number;
    data: T;
    message: any;
    response?: AxiosResponse;
    success?: boolean;
}

interface ParamsOfConfig {
    [index: string]: any;
}

const request = axios.create({
    timeout: 300000,
    responseType: 'json',
    validateStatus: (status: number) => (status >= 200 && status < 300) || status === 304
});

const wrapRequest = <T = any>(config: RequestConfig): Promise<ApiResult<T>> => {
    const { url = '', method, params = {}, data = {}, notifyBizError } = config;
    const requestParams = {
        ...config,
        headers: {
            'Content-Type': 'application/json',
            ...(config.headers || {}),
            'accept-language': getValue(config.headers, 'accept-language', getCurrentLocale())
        }
    };
    const axiosPromise = request(requestParams)
        .catch((err) => {
            const history = window.routerHistory;
            // catch网络层错误
            let code;
            // 判断是否是xhr取消请求
            if (!axios.isCancel(err)) {
                const errMsg = JSON.stringify(err && err.message);
                if (err.response) {
                    code = HTTP_ERROR_CODE;
                    const { status, headers, data } = err.response;
                    if (status === 401) {
                        config.notifyHttpError = false;
                        history && history.replace('/login');
                    }
                }
                // 网络错误抛出错误
                if (config.notifyHttpError) {
                    let msgLang = window.navigator.onLine
                        ? 'MessageHttpError'
                        : 'MessageNetworkDisconnected';
                    if (errMsg === '"Network Error"') msgLang = 'MessageNetworkError';
                    message.error(intl.get(msgLang));
                }
            } else {
                code = CANCEL_CODE;
            }
            return Promise.reject({
                code,
                ...err
            });
        })
        .then((res) => {
            const data = getValue(res, 'data', {});
            const success = getValue(data, 'success');
            if (success === false || !data || (success === undefined && data.code !== 0)) {
                // 后端服务校验失败是提示
                const msg = data ? data.message || data.msg : '';
                if (msg && notifyBizError) {
                    message.error(msg);
                }
                return Promise.reject(data);
            }
            return data;
        });
    return axiosPromise as Promise<ApiResult<T>>;
};

export const post = <T = any>(
    url: string,
    data?: any,
    config?: RequestConfig
): Promise<ApiResult<T>> => wrapRequest<T>({ url, data, method: 'post', ...config });

export const get = <T = any>(
    url: string,
    params?: ParamsOfConfig,
    config?: RequestConfig
): Promise<ApiResult<T>> => wrapRequest<T>({ url, params, method: 'get', ...config });
export default wrapRequest;
